import { supabase } from '@/lib/supabase';
import { DOCX_TEMPLATE_MIME } from '@/lib/patientReports/docxTemplate';

/** Signed-in user's display name. Read live. Not stored on a template. */
export async function loadReportAuthorName(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) return '';
  const profile = await supabase.from('users').select('name').eq('id', data.user.id).maybeSingle();
  if (profile.error) return '';
  return profile.data?.name?.trim() ?? '';
}

/**
 * Read a stored report object.
 * Replacing a Word template keeps the same path, so this must not reuse a
 * cached copy of the previous file. A signed URL is unique per request.
 */
export async function downloadPatientReportObject(path: string): Promise<Uint8Array> {
  const signed = await supabase.storage.from('patient-reports').createSignedUrl(path, 60);
  if (!signed.error && signed.data?.signedUrl) {
    try {
      const response = await fetch(signed.data.signedUrl, { cache: 'no-store' });
      if (response.ok) return new Uint8Array(await response.arrayBuffer());
    } catch {
      // The authenticated download below still returns the object.
    }
  }
  const { data, error } = await supabase.storage.from('patient-reports').download(path);
  if (error || !data) throw error ?? new Error('download');
  return new Uint8Array(await data.arrayBuffer());
}

/** Download name for a stored Word template. The original upload name is not kept. */
export function wordTemplateFileName(label: string): string {
  let cleaned = '';
  for (const char of label) {
    const code = char.charCodeAt(0);
    cleaned += code < 32 || '\\/:*?"<>|'.includes(char) ? ' ' : char;
  }
  cleaned = cleaned.replace(/\s+/g, ' ').trim().slice(0, 80);
  const base = cleaned || 'Word-skabelon';
  return base.toLowerCase().endsWith('.docx') ? base : `${base}.docx`;
}

export async function downloadStoredDocx(path: string): Promise<Uint8Array> {
  return downloadPatientReportObject(path);
}

export function saveDocxFile(bytes: Uint8Array, filename: string): void {
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  const url = URL.createObjectURL(new Blob([copy], { type: DOCX_TEMPLATE_MIME }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.toLowerCase().endsWith('.docx') ? filename : `${filename}.docx`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function uploadDocxTemplate(path: string, bytes: Uint8Array): Promise<void> {
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  const { error } = await supabase.storage
    .from('patient-reports')
    .upload(path, new Blob([copy], { type: DOCX_TEMPLATE_MIME }), {
      contentType: DOCX_TEMPLATE_MIME,
      cacheControl: '0',
      upsert: true,
    });
  if (error) throw error;
}
